import React, { useEffect, useRef, useState } from "react";
import { Table, Spinner } from "reactstrap";

/**
 * Resident list — Unit Number, Name, Contact Number, Email, and the unit-level
 * Garage Code / Garbage Bin / Composite Bin columns.
 *
 * People are boxed per unit (per tenancy window): lease holders first, then
 * occupants, each labelled and styled so the two are never mistaken for one
 * another. Contact Number and Email are editable in place for CURRENT residents
 * and save to the Resident Directory's own store on blur or Enter. Past
 * residents come from archived items and historic snapshots, which are records
 * of what was true at the time, so their contact is shown read-only. A vacant
 * unit (from the live mastersheet) is its own box with a Vacant marker.
 */
const cell = (value) => (String(value ?? "").trim() ? String(value).trim() : "—");

const rowKey = (row, i) => `${row.buildingName}-${row.unitNo}-${row.moveIn}-${i}`;

/** Current tenancies can be edited; edits save to the Resident Directory store. */
export const isRowEditable = (row) =>
  row?.status === "current" && (row?.source === "active" || row?.source === "directory");

/**
 * One box per unit per tenancy window. Occupants belong to a tenancy, so a
 * unit's past and current tenancies stay in separate boxes rather than being
 * mixed into one household that never existed.
 */
export const groupRowsByUnit = (rows) => {
  const groups = [];
  const index = new Map();
  (rows || []).forEach((row) => {
    const key =
      row.status === "current"
        ? [row.buildingName ?? "", row.unitNo, "current"].join("|")
        : [row.buildingName ?? "", row.unitNo, row.moveIn ?? "", row.moveOut ?? ""].join("|");
    if (!index.has(key)) {
      index.set(key, groups.length);
      groups.push({ key, rows: [] });
    }
    groups[index.get(key)].rows.push(row);
  });
  for (const group of groups) {
    group.rows.sort((a, b) => {
      if (a.role !== b.role) return a.role === "occupant" ? 1 : -1;
      return String(a.name).localeCompare(String(b.name));
    });
  }
  return groups;
};

const EditableCell = ({ row, field, type, placeholder, label, onSave }) => {
  const original = row[field] ?? "";
  const [value, setValue] = useState(original);
  const [state, setState] = useState("idle"); // idle | saving | saved | error
  const inputRef = useRef(null);

  // Keep in step when the directory reloads underneath us.
  useEffect(() => {
    setValue(row[field] ?? "");
  }, [row[field]]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = async () => {
    const next = String(value ?? "").trim();
    if (next === String(original ?? "").trim()) return;
    setState("saving");
    try {
      await onSave(next);
      setState("saved");
      setTimeout(() => setState((s) => (s === "saved" ? "idle" : s)), 1600);
    } catch (err) {
      setState("error");
      setValue(original ?? ""); // put the old value back so nothing looks saved
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      inputRef.current?.blur();
    }
    if (e.key === "Escape") {
      setValue(original ?? "");
      setState("idle");
      inputRef.current?.blur();
    }
  };

  const border =
    state === "error" ? "#DC2626" : state === "saved" ? "#16A34A" : "transparent";

  return (
    <span className="rd-edit">
      <input
        ref={inputRef}
        type={type}
        className="rd-edit-input"
        style={{ borderColor: border }}
        value={value}
        placeholder={placeholder}
        aria-label={`${label} for ${row.name || `unit ${row.unitNo}`}`}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
        disabled={state === "saving"}
      />
      {state === "saving" && <Spinner size="sm" className="rd-edit-spin" />}
      {state === "saved" && (
        <i className="mdi mdi-check rd-edit-ok" title="Saved to the Resident Directory" />
      )}
      {state === "error" && (
        <i className="mdi mdi-alert-circle-outline rd-edit-err" title="Could not save — reverted" />
      )}
    </span>
  );
};

/**
 * Contact rules, mirrored from the server (resident-directory.ts). Numbers: digits only, at
 * least ten, two per person. Emails: real addresses, two per person. Both are stored joined
 * as "first; second" (older records used " / "; both split).
 */
export const PHONE_MIN_DIGITS = 10;
export const PHONE_MAX_DIGITS = 15;
export const MAX_PHONES = 2;
export const MAX_EMAILS = 2;
export const CONTACT_JOINER = "; ";
export const PHONE_JOINER = CONTACT_JOINER;
export const splitContactValues = (value) =>
  String(value ?? "")
    .split(/\s*[;,/]\s*|\s{2,}/g)
    .map((p) => p.trim())
    .filter(Boolean);
export const splitPhones = splitContactValues;
/** Why a typed number cannot be saved yet, or null. Blank is allowed (it clears). */
export const phoneProblem = (digits) =>
  digits && digits.length < PHONE_MIN_DIGITS ? `At least ${PHONE_MIN_DIGITS} digits` : null;
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const emailProblem = (value) => (value && !EMAIL_RX.test(value) ? "Enter a valid email" : null);

const CONTACT_KINDS = {
  phone: {
    max: MAX_PHONES,
    type: "tel",
    inputMode: "numeric",
    pattern: "[0-9]*",
    maxLength: PHONE_MAX_DIGITS,
    sanitize: (raw) => raw.replace(/\D+/g, "").slice(0, PHONE_MAX_DIGITS),
    problem: phoneProblem,
    labels: ["Contact number", "Second contact number"],
    placeholders: ["Add number", "Second number"],
    addLabel: "Add a second number",
    removeLabel: "Remove second number",
  },
  email: {
    max: MAX_EMAILS,
    type: "email",
    inputMode: "email",
    pattern: undefined,
    maxLength: 120,
    sanitize: (raw) => raw.replace(/\s+/g, "").slice(0, 120),
    problem: emailProblem,
    labels: ["Email", "Second email"],
    placeholders: ["Add email", "Second email"],
    addLabel: "Add a second email",
    removeLabel: "Remove second email",
  },
};

/**
 * One or two values of a kind (numbers or emails) for a person. Typing is kept to what the
 * kind allows; a value that is not acceptable yet is flagged once the field is left and is
 * never saved. "+" opens a second line, "−" removes it (and saves the remaining value straight
 * away). Saves on blur or Enter, like the other cells.
 */
const MultiValueCell = ({ row, field, kind, onSave }) => {
  const cfg = CONTACT_KINDS[kind];
  const original = row[field] ?? "";
  const fromRow = () => {
    const list = splitContactValues(original).slice(0, cfg.max);
    return list.length ? list : [""];
  };
  const [values, setValues] = useState(fromRow);
  const [touched, setTouched] = useState([]);
  const [state, setState] = useState("idle"); // idle | saving | saved | error
  const refs = useRef([]);

  // Keep in step when the directory reloads underneath us.
  useEffect(() => {
    setValues(fromRow());
    setTouched([]);
  }, [row[field]]); // eslint-disable-line react-hooks/exhaustive-deps

  const joined = (list) => list.map((v) => v.trim()).filter(Boolean).join(CONTACT_JOINER);
  const problems = values.map(cfg.problem);
  const who = row.name || `unit ${row.unitNo}`;

  const commit = async (list = values) => {
    if (list.some(cfg.problem)) return; // nothing half-typed is ever saved
    // An emptied second line simply goes away.
    const tidy = list.length === 2 && !list[1].trim() ? [list[0]] : list;
    if (tidy.length !== list.length) setValues(tidy);
    const next = joined(tidy);
    if (next === joined(splitContactValues(original))) return;
    setState("saving");
    try {
      await onSave(next);
      setState("saved");
      setTimeout(() => setState((s) => (s === "saved" ? "idle" : s)), 1600);
    } catch (err) {
      setState("error");
      setValues(fromRow()); // put the old value back so nothing looks saved
    }
  };

  const setAt = (i, raw) => setValues((prev) => prev.map((v, idx) => (idx === i ? cfg.sanitize(raw) : v)));
  const markTouched = (i) =>
    setTouched((t) => {
      const next = [...t];
      next[i] = true;
      return next;
    });
  const add = () => {
    if (values.length >= cfg.max) return;
    setValues((prev) => [...prev, ""]);
    setTimeout(() => refs.current[1]?.focus(), 0);
  };
  const remove = (i) => {
    const list = values.filter((_, idx) => idx !== i);
    const next = list.length ? list : [""];
    setValues(next);
    setTouched([]);
    commit(next);
  };
  const onKeyDown = (i) => (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      refs.current[i]?.blur();
    }
    if (e.key === "Escape") {
      setValues(fromRow());
      setTouched([]);
      setState("idle");
      refs.current[i]?.blur();
    }
  };

  const border = state === "error" ? "#DC2626" : state === "saved" ? "#16A34A" : "transparent";

  return (
    <span className="rd-edit rd-multi">
      {values.map((v, i) => {
        const problem = touched[i] ? problems[i] : null;
        return (
          <span className="rd-multi-line" key={i}>
            <input
              ref={(el) => {
                refs.current[i] = el;
              }}
              type={cfg.type}
              inputMode={cfg.inputMode}
              pattern={cfg.pattern}
              maxLength={cfg.maxLength}
              className="rd-edit-input"
              style={{ borderColor: problem ? "#DC2626" : border }}
              value={v}
              placeholder={cfg.placeholders[i] || cfg.placeholders[1]}
              aria-label={`${cfg.labels[i] || cfg.labels[1]} for ${who}`}
              aria-invalid={!!problem}
              title={problem || undefined}
              onChange={(e) => setAt(i, e.target.value)}
              onBlur={() => {
                markTouched(i);
                commit();
              }}
              onKeyDown={onKeyDown(i)}
              disabled={state === "saving"}
            />
            {i === 0 && values.length < cfg.max ? (
              <button
                type="button"
                className="rd-multi-btn"
                aria-label={`${cfg.addLabel} for ${who}`}
                title={cfg.addLabel}
                onClick={add}
                disabled={state === "saving"}
              >
                +
              </button>
            ) : null}
            {i > 0 ? (
              <button
                type="button"
                className="rd-multi-btn"
                aria-label={`${cfg.removeLabel} for ${who}`}
                title="Remove"
                onClick={() => remove(i)}
                disabled={state === "saving"}
              >
                −
              </button>
            ) : null}
            {problem ? <span className="rd-multi-hint">{problem}</span> : null}
          </span>
        );
      })}
      {state === "saving" && <Spinner size="sm" className="rd-edit-spin" />}
      {state === "saved" && (
        <i className="mdi mdi-check rd-edit-ok" title="Saved to the Resident Directory" />
      )}
      {state === "error" && (
        <i className="mdi mdi-alert-circle-outline rd-edit-err" title="Could not save — reverted" />
      )}
    </span>
  );
};

const PhoneCell = ({ row, onSave }) => <MultiValueCell row={row} field="phone" kind="phone" onSave={onSave} />;
const EmailCell = ({ row, onSave }) => <MultiValueCell row={row} field="email" kind="email" onSave={onSave} />;

/** Read-only values, one per line, or a dash. */
const MultiText = ({ value }) => {
  const list = splitContactValues(value);
  if (!list.length) return "—";
  return (
    <span className="rd-multi-text">
      {list.map((v, i) => (
        <span key={i}>{v}</span>
      ))}
    </span>
  );
};

const UNIT_FIELDS = [
  { field: "garageCode", patchKey: "garage_code", label: "Garage code", header: "Garage Code" },
  { field: "garbageBin", patchKey: "garbage_bin", label: "Garbage bin", header: "Garbage Bin" },
  { field: "compositeBin", patchKey: "composite_bin", label: "Composite bin", header: "Composite Bin" },
];

const ResidentTable = ({ rows, onSaveContact, onSaveUnit }) => (
  <div className="rd-table-wrap">
    <Table responsive className="rd-table mb-0">
      <thead>
        <tr>
          <th scope="col">Unit Number</th>
          <th scope="col">Name</th>
          <th scope="col">Contact Number</th>
          <th scope="col">Email</th>
          {UNIT_FIELDS.map((f) => (
            <th scope="col" key={f.field}>{f.header}</th>
          ))}
        </tr>
      </thead>
      {groupRowsByUnit(rows).map((group) => {
        const firstRow = group.rows[0];
        const unitEditable =
          group.rows.some((r) => isRowEditable(r)) && typeof onSaveUnit === "function";
        return (
          <tbody className="rd-unit-group" key={group.key}>
            {group.rows.map((row, i) => {
              // A vacant unit's placeholder has no person to edit; its unit cells still are.
              const editable = !row.vacant && isRowEditable(row) && typeof onSaveContact === "function";
              // Names live on the mastersheet unit now, so a live row's name can be
              // corrected here the same way its contacts can.
              const nameEditable = editable && typeof onSaveUnit === "function";
              const isOccupant = row.role === "occupant";
              return (
                <tr key={rowKey(row, i)} className={row.vacant ? "rd-row-vacant" : isOccupant ? "rd-row-occupant" : "rd-row-tenant"}>
                  {i === 0 && (
                    <td className="rd-unit-cell" rowSpan={group.rows.length}>
                      {cell(row.unitNo)}
                    </td>
                  )}
                  <td>
                    <span className="rd-person">
                      {row.vacant ? (
                        <span className="rd-vacant-pill">Vacant</span>
                      ) : nameEditable ? (
                        <EditableCell
                          row={row}
                          field="name"
                          type="text"
                          placeholder="Name"
                          label="Resident name"
                          onSave={(v) =>
                            onSaveUnit(row, { name_edit: { from: row.name, to: v, role: row.role } })
                          }
                        />
                      ) : (
                        <span className="rd-person-name">{cell(row.name)}</span>
                      )}
                      {!row.vacant && (
                        <span className={`rd-role-pill ${isOccupant ? "is-occupant" : "is-tenant"}`}>
                          {isOccupant ? "Occupant" : "Lease Holder"}
                        </span>
                      )}
                    </span>
                  </td>
                  <td>
                    {editable ? (
                      <PhoneCell row={row} onSave={(v) => onSaveContact(row, { phone: v })} />
                    ) : (
                      <MultiText value={row.phone} />
                    )}
                  </td>
                  <td>
                    {editable ? (
                      <EmailCell row={row} onSave={(v) => onSaveContact(row, { email: v })} />
                    ) : (
                      <MultiText value={row.email} />
                    )}
                  </td>
                  {i === 0 &&
                    UNIT_FIELDS.map((f) => (
                      <td className="rd-unit-field-cell" rowSpan={group.rows.length} key={f.field}>
                        {unitEditable ? (
                          <EditableCell
                            row={firstRow}
                            field={f.field}
                            type="text"
                            placeholder="Add"
                            label={f.label}
                            onSave={(v) => onSaveUnit(firstRow, { [f.patchKey]: v })}
                          />
                        ) : (
                          cell(firstRow[f.field])
                        )}
                      </td>
                    ))}
                </tr>
              );
            })}
          </tbody>
        );
      })}
    </Table>
  </div>
);

export { ResidentTable };
